"use client";

import * as React from "react";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { BuyBoxActions } from "~/components/product/actions";
import { FlashSaleCountdown } from "~/components/product/flash-sale-countdown";
import {
  CustomOptions,
  usePersonalisation,
} from "~/components/product/personalisation";
import { PriceBlock } from "~/components/product/PriceBlock";
import { BnplMessage } from "~/components/payments/bnpl-message";
import { deriveStockStatus, StockLabel } from "~/components/product/StockLabel";
import {
  TrustDelivery,
  type TrustDeliveryProps,
} from "~/components/product/TrustDelivery";
import { SubscribeUpsell } from "~/components/product/SubscribeUpsell";
import {
  SizeSelector,
  SwatchGroup,
  useVariant,
} from "~/components/product/variant";
import { LOW_STOCK_THRESHOLD, SWATCH_CONFIG } from "~/config/storefront";
import { LocaleLink, useTranslator } from "~/i18n/provider";
import type { CartPersonalisation } from "~/lib/cart/cart-interface";
import { formatMoney } from "~/lib/money";
import { categoryHref } from "~/lib/storefront/category-href";
import { swatchBackground } from "~/lib/colour-swatch";
import { cn } from "~/lib/utils";
import type {
  GiftCardConfig,
  SaleBadgeStyle,
  TaxMode,
} from "~/server/api/schemas/brand-settings";
import type { PdpProduct } from "~/server/api/schemas/pdp";
import { GiftCardPurchaseForm } from "~/components/product/gift-card-purchase-form";
import { ProductFacts } from "~/components/product/ProductFacts";
import { PdpShippingEstimate } from "~/components/product/PdpShippingEstimate";

export interface BuyBoxCommercialConfig {
  taxMode: TaxMode;
  /** P11-13 · The brand's tax word, so the PDP does not say "VAT" in the US. */
  taxLabel?: string;
  /** P11-13 · False when no rate anywhere is above zero — omit the tax note. */
  showTaxNote?: boolean;
  saleBadgeStyle: SaleBadgeStyle;
  showSavingsPercent: boolean;
  /** P6-14 · Brand gift-card config (custom-amount + expiry), for the purchase form. */
  giftCard: GiftCardConfig;
  /**
   * P11-20 · The brand's BASE currency. Optional so existing callers keep
   * compiling; without it the gift-card issuance disclosure simply doesn't
   * render, which is correct for a brand that never converts.
   */
  baseCurrency?: string;
  deliveryCountry?: string;
  brandId?: string;
}

function filteredCategoryHref(
  categorySlug: string,
  param: "brand" | "tag",
  value: string,
): string {
  const query = new URLSearchParams({ [param]: value });
  return `${categoryHref(categorySlug)}?${query}`;
}

export function BuyBoxCommercial({
  product,
  config,
  trustModule,
  signedIn,
  inboundExpected,
  preview = false,
}: {
  product: PdpProduct;
  config: BuyBoxCommercialConfig;
  /**
   * P3-11 trust/delivery block, computed server-side (brand tz + config) and
   * passed through this client boundary as plain data; null when the brand has
   * the module flag off.
   */
  trustModule?: TrustDeliveryProps | null;
  /**
   * P3-09: resolved server-side (no storefront SessionProvider) and threaded to
   * the wishlist actions to pick server vs local persistence.
   */
  signedIn: boolean;
  /**
   * P17-16 · Pre-formatted "expected" dates by variant id, for pre-order and
   * backorder lines with an OPEN inbound shipment behind them.
   *
   * A map rather than one date because the shopper switches variant client-side
   * and the answer changes with them, and pre-formatted because the server owns
   * both the locale tag and the UTC calendar day — see the PDP page. Undefined
   * (the usual case) means the flag is off or nothing is on order, and the line
   * simply does not render.
   */
  inboundExpected?: Record<string, string>;
  /** Saved admin preview: show commercial presentation without purchase actions. */
  preview?: boolean;
}) {
  const t = useTranslator();
  const { selected, resolvedVariant } = useVariant();
  const selectedValues = new Set(Object.values(selected));
  const [estimateQuantity, setEstimateQuantity] = React.useState(1);

  // P6-12 · Flash-sale price impression (once per PDP view with an active sale).
  const activeSalePromotionId =
    product.activeSale && !product.activeSale.teaser
      ? product.activeSale.promotionId
      : null;
  React.useEffect(() => {
    if (activeSalePromotionId) {
    }
  }, [activeSalePromotionId, product.id]);
  const hasRange =
    product.priceMinMinor !== null &&
    product.priceMaxMinor !== null &&
    product.priceMinMinor < product.priceMaxMinor;
  // Once a variant resolves, price/compare-at/stock come from THAT variant —
  // never blended with product-level aggregates. A resolved variant with a
  // null compare-at is simply not on sale; falling back to the product's
  // compareAtMinMinor (the cheapest variant's) would paint a false "was" price.
  const priceMinor = resolvedVariant
    ? resolvedVariant.priceMinor
    : product.priceMinMinor;
  const compareAtMinor = resolvedVariant
    ? resolvedVariant.compareAtMinor
    : product.compareAtMinMinor;
  const currency = resolvedVariant
    ? resolvedVariant.currency
    : product.currency;
  // Concrete per-variant stock only once resolved; before that show the
  // product-level availability, not a cross-variant stock *sum* (which would
  // read as a bogus "Only N left").
  const stockStatus = resolvedVariant
    ? deriveStockStatus({
        stock: resolvedVariant.stock,
        lowStockThreshold: LOW_STOCK_THRESHOLD,
        isPreorder: resolvedVariant.isPreorder,
        backorderable: resolvedVariant.backorderable,
      })
    : product.inStock
      ? ({ label: "In stock", tone: "success" } as const)
      : ({ label: "Out of stock", tone: "danger" } as const);
  const expectedLabel = resolvedVariant
    ? (inboundExpected?.[resolvedVariant.id] ?? null)
    : null;
  const primaryCategory = product.categories[0] ?? null;

  // P3-07 · Personalisation. The hook is always called (inert for a product
  // with no custom options); the flag only gates the UI. The delta shown here
  // is display-only — add-to-cart (P3-08) recomputes it server-side via
  // `validatePersonalisation`, and gates on `personalisation.isValid`.
  const personalisation = usePersonalisation(product.customOptions);
  const personalisationEnabled =
    useFeatureFlag("pdpPersonalisation") && personalisation.defs.length > 0;
  const giftCardsEnabled = useFeatureFlag("giftCards");
  const personalisationDelta = personalisationEnabled
    ? personalisation.priceDeltaMinor
    : 0;
  const totalMinor =
    priceMinor !== null ? priceMinor + personalisationDelta : null;
  // Opaque payload carried through add-to-cart (P3-08); P4's mutation
  // re-validates and recomputes the delta server-side. Empty/unchecked fields
  // are dropped so the line only records what the shopper actually chose.
  const personalisationPayload: CartPersonalisation = personalisationEnabled
    ? Object.fromEntries(
        Object.entries(personalisation.values)
          .filter(([, v]) => v !== "" && v !== false)
          .map(([k, v]) => [k, String(v)]),
      )
    : null;

  const teaser = product.shortDescription ?? product.description;

  // P6-14 · A gift-card product replaces the standard variant/add-to-cart UI
  // with the recipient + amount purchase form.
  if (product.isGiftCard) {
    return (
      <section aria-label={t("pdp.buyGiftCard")} data-testid="pdp-buy-box">
        <h1 className="font-heading text-2xl font-semibold text-fg sm:text-3xl">
          {product.title}
        </h1>
        {teaser && (
          <p className="mt-4 max-w-prose text-sm leading-relaxed text-fg-muted">
            {teaser}
          </p>
        )}
        {preview ? (
          <p className="mt-6 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-fg-muted">
            Preview only. Gift card purchase is disabled.
          </p>
        ) : giftCardsEnabled ? (
          <GiftCardPurchaseForm
            product={product}
            config={config.giftCard}
            baseCurrency={config.baseCurrency}
          />
        ) : (
          <p className="mt-6 text-sm text-fg-muted">
            {t("pdp.giftCardsUnavailable")}
          </p>
        )}
      </section>
    );
  }

  return (
    <section aria-label={t("pdp.purchaseOptions")} data-testid="pdp-buy-box">
      {product.manufacturer && (
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          {primaryCategory ? (
            <LocaleLink
              href={filteredCategoryHref(
                primaryCategory.slug,
                "brand",
                product.manufacturer.id,
              )}
              className="underline-offset-4 hover:text-fg hover:underline"
            >
              {product.manufacturer.name}
            </LocaleLink>
          ) : (
            product.manufacturer.name
          )}
        </p>
      )}
      <h1 className="mt-1 font-heading text-2xl font-semibold text-fg sm:text-3xl">
        {product.title}
      </h1>

      <div className="mt-4">
        <PriceBlock
          priceMinor={priceMinor}
          compareAtMinor={compareAtMinor}
          currency={currency}
          taxMode={config.taxMode}
          taxLabel={config.taxLabel}
          showTaxNote={config.showTaxNote}
          from={!resolvedVariant && hasRange}
          saleBadgeStyle={config.saleBadgeStyle}
          showSavingsPercent={config.showSavingsPercent}
        />
        {/* P17-10 · Directly under the price, which is where a shopper decides
            whether the number is affordable — and self-gating, so a shop with
            the flag off renders nothing here and loads no payments SDK. */}
        <BnplMessage
          amountMinor={priceMinor}
          currency={currency}
          className="mt-1.5"
        />
        {product.activeSale ? (
          <FlashSaleCountdown
            sale={product.activeSale}
            className="mt-1 block text-xs font-medium text-danger tabular-nums"
          />
        ) : null}
      </div>

      {(resolvedVariant?.description ?? teaser) && (
        <p className="mt-4 max-w-prose text-sm leading-relaxed text-fg-muted">
          {resolvedVariant?.description ?? teaser}
        </p>
      )}

      {product.options.map((option) =>
        // COLOUR axes render the P3-05 swatch radiogroup; SIZE axes the P3-06
        // size selector (both drive gallery/price/availability via the engine).
        // SR-06 follow-up · OTHER axes (an imported "Design", "Style"…) use the
        // same button radiogroup, without a size guide: as read-only chips a
        // shopper could not choose them, and the cart got the default variant.
        // TEXT axes keep the read-only chips (P3-07 owns custom input).
        option.type === "COLOUR" ? (
          <SwatchGroup
            key={option.id}
            option={option}
            config={SWATCH_CONFIG}
            className="mt-5"
          />
        ) : option.type === "SIZE" || option.type === "OTHER" ? (
          <SizeSelector
            key={option.id}
            option={option}
            sizeGuideHtml={
              option.type === "SIZE" ? product.sizeGuideHtml : undefined
            }
            className="mt-5"
          />
        ) : (
          <div key={option.id} className="mt-5">
            <p className="text-sm font-medium text-fg">{option.name}</p>
            <div
              data-testid={`pdp-option-${option.name.toLowerCase()}`}
              className="mt-2 flex flex-wrap gap-2"
            >
              {option.values.map((value) => (
                <span
                  key={value.id}
                  data-selected={selectedValues.has(value.id) || undefined}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm",
                    selectedValues.has(value.id)
                      ? "border-fg text-fg"
                      : "border-border text-fg-muted",
                  )}
                >
                  {value.swatchHex && (
                    <span
                      aria-hidden
                      className="size-3.5 rounded-full border border-border"
                      style={{
                        background:
                          swatchBackground(value.value, value.swatchHex) ??
                          undefined,
                      }}
                    />
                  )}
                  {value.value}
                </span>
              ))}
            </div>
          </div>
        ),
      )}

      <ProductFacts
        product={product}
        variant={resolvedVariant ?? null}
        hasSizeOption={product.options.some((option) => option.type === "SIZE")}
      />

      {personalisationEnabled && (
        <CustomOptions
          defs={personalisation.defs}
          state={personalisation}
          currency={currency}
          className="mt-1"
        />
      )}

      {personalisationEnabled &&
        personalisationDelta > 0 &&
        totalMinor !== null && (
          <p
            data-testid="pdp-personalisation-total"
            className="mt-3 text-sm text-fg"
          >
            Personalisation{" "}
            <span className="font-medium">
              +{formatMoney(personalisationDelta, currency)}
            </span>{" "}
            · Total{" "}
            <span className="font-semibold">
              {formatMoney(totalMinor, currency)}
            </span>
          </p>
        )}

      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1">
        <StockLabel status={stockStatus} />
        {/* P17-16 · Only ever beside a Pre-order / Available-to-backorder
            label: the resolver upstream returns nothing for an in-stock line,
            a line with nothing on order, or a date already past. */}
        {expectedLabel ? (
          <p data-testid="pdp-expected" className="text-sm text-fg-muted">
            {t("pdp.expectedDate", { date: expectedLabel })}
          </p>
        ) : null}
        {resolvedVariant?.sku && (
          <p data-testid="pdp-sku" className="text-xs text-fg-muted">
            SKU:{" "}
            <span className="font-mono text-fg">{resolvedVariant.sku}</span>
          </p>
        )}
      </div>

      {(product.categories.length > 0 || product.tags.length > 0) && (
        <div
          aria-label={t("pdp.categoriesAndTags")}
          className="mt-4 flex flex-wrap gap-2"
        >
          {product.categories.map((category) => (
            <LocaleLink
              key={category.id}
              href={categoryHref(category.slug)}
              className="rounded-full border border-border px-2.5 py-1 text-xs text-fg-muted hover:border-fg hover:text-fg"
            >
              {category.name}
            </LocaleLink>
          ))}
          {product.tags.map((tag) =>
            // Tag chips route to the category-scoped PLP filter, so they can
            // only link when the product has a category. Without one they still
            // render (as plain chips) rather than being silently dropped.
            primaryCategory ? (
              <LocaleLink
                key={tag.id}
                href={filteredCategoryHref(
                  primaryCategory.slug,
                  "tag",
                  tag.slug,
                )}
                className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-fg-muted hover:text-fg"
              >
                #{tag.name}
              </LocaleLink>
            ) : (
              <span
                key={tag.id}
                className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-fg-muted"
              >
                #{tag.name}
              </span>
            ),
          )}
        </div>
      )}

      {/* Required-personalisation gate (P3-07): the CTA below stays disabled
          until `personalisation.isValid`. Surfaced as an inline hint so the
          block is clear before the shopper reaches the button. */}
      {personalisationEnabled && !personalisation.isValid && (
        <p
          data-testid="pdp-personalisation-hint"
          className="mt-4 text-xs text-fg-muted"
        >
          {t("pdp.personalisationRequired")}
        </p>
      )}

      {/* P3-08 · quantity stepper + add-to-cart / buy-now + mobile sticky bar,
          bound to the resolved variant's stock via the engine and (P3-07) gated
          on required personalisation, carrying the chosen values into the cart. */}
      {preview ? (
        <p className="mt-5 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-fg-muted">
          Preview only. Cart, wishlist and checkout actions are disabled.
        </p>
      ) : (
        <BuyBoxActions
          productId={product.id}
          title={product.title}
          signedIn={signedIn}
          personalisation={personalisationPayload}
          personalisationValid={
            !personalisationEnabled || personalisation.isValid
          }
          onQuantityChange={setEstimateQuantity}
        />
      )}

      {!preview && !product.isVirtual ? (
        <PdpShippingEstimate
          variantId={resolvedVariant?.id ?? null}
          quantity={estimateQuantity}
          defaultCountry={config.deliveryCountry}
          recoveryScope={config.brandId}
        />
      ) : null}

      {/* P7-14B · Subscribe & save (flag + per-variant plans; renders nothing
          otherwise). Routes to the dedicated subscribe lane, not the cart. */}
      {!preview && (
        <SubscribeUpsell
          variantId={resolvedVariant?.id ?? null}
          currency={product.currency}
          signedIn={signedIn}
        />
      )}

      {/* P3-11 · Delivery estimate + returns/warranty + trust/payment badges,
          all brand-config-driven (P3-DB01 buy-box slot 10). */}
      {trustModule && <TrustDelivery {...trustModule} className="mt-6" />}
    </section>
  );
}
